import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

/** List-page filters, kept in the URL instead of component state.
 *
 * The dashboards already work this way; the list pages did not, so a filter
 * survived paging (the request closure reads it on every fetch) but nothing
 * else. Refreshing lost it, sending someone the link showed them the
 * unfiltered list, and a screenshot of "no records" could not be reproduced
 * from the URL it was taken at.
 *
 * Values are strings, because that is what a URL holds. Callers that need a
 * number convert at the point of use rather than the hook guessing, so a
 * malformed `?fy=abc` stays visible instead of silently becoming NaN.
 *
 * `keys` must be a stable reference - declare it as a module-level `const
 * FILTERS = [...] as const`, not inline, or the memo recomputes every render.
 */
export function useUrlFilters<K extends string>(keys: readonly K[]) {
  const [params, setParams] = useSearchParams();

  const values = useMemo(() => {
    const out = {} as Record<K, string | undefined>;
    for (const key of keys) out[key] = params.get(key) ?? undefined;
    return out;
  }, [params, keys]);

  const setFilters = useCallback(
    (patch: Partial<Record<K, string | number | undefined | null>>) => {
      setParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          for (const [key, value] of Object.entries(patch)) {
            // Cleared filters leave the URL rather than sitting there as
            // `?keyword=`, so a shared link carries only what was chosen.
            if (value === undefined || value === null || value === '') {
              next.delete(key);
            } else {
              next.set(key, String(value));
            }
          }
          return next;
        },
        // replace, not push: a search box would otherwise put an entry in the
        // history for every term tried, and Back would walk them one at a time
        // instead of leaving the page.
        { replace: true },
      );
    },
    [setParams],
  );

  return [values, setFilters] as const;
}

/** Parse a filter value that addresses a row by id. */
export function asNumber(value: string | undefined) {
  if (value === undefined || value === '') return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}
